import React, { useCallback, useEffect, useState } from 'react';
import { JSXIf } from '@framework-controls/jsxconditions';
import { LoadingProgress } from '@framework-controls/core';
import { IView } from 'Framework';
import { strings } from 'src/localization';
import css from './AddVehicleFooter.module.scss';
import SVPButton from '../../SVPButtons/SVPButton';
import { NewVehicle, SpecData, UpdateApptVehInp, VehicleRec, VehicleRecs, VehData, UpdateApptOpsInp, AddVehicleProperties, ApptVeh, DecodeRec, ascCtaInteractionInput, ascRetailProcessInput, ascElementConfigurationInput } from 'src/models';
import { ComponentMapIds } from 'src/components/map';
import { ViewMode, VINEntryFlag } from 'src/enums';
import { IServicesState } from 'src/store/states';
import { ascCtaInteraction, ascRetailProcess, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { getProviderId, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

interface IProps {
    newVehicleInfo?: NewVehicle;
    vehicleSpecs?: SpecData;
    mainPaneViewOrder?: IView[];
    vehicles: VehicleRecs;
    specProfile: string;
    store: string;
    branch: string;
    additionalServices: IServicesState;
    apptVin?: string;
    showWeOwes?: boolean;
    showOEMRecall?: boolean;
    showPrvRecom?: boolean;
    inquiryInvoked: boolean;
    viewMode: ViewMode;
    menusReturnCode: string;
    currentProperty: AddVehicleProperties;
    selectedVehicle: ApptVeh;
    vehSelectedVehicle: string;
    nameId: string,
    skipServices: boolean,
    apptDept: string;
    locale: string;
    addVinError: string;
    TMNAMileage: number;
    theme: string;
    countryCode: string;
    login: boolean;
    multipleCustomers?: boolean;
    vehicleAddTriggered: boolean;
    popFromMainPane?: (id: string) => void;
    pushToMainPane: (inputView: IView) => void;
    clearNewVehicle: () => void;
    updateAppVehicle: (inp: UpdateApptVehInp) => void;
    estMilChange: (id: string, value: string) => void;
    clearMenusState: () => void;
    getMenus: (value1, value2) => void;
    getAdditionalServices: (profileNum: string, store: string, branch: string) => void;
    selectVehicle: (id: string) => void;
    addVehicle: (newVehicle: VehicleRec) => void;
    getApptOps: (profileNum: string, store: string, branch: string, vehData?: VehData) => void;
    clearAdditionalServices: () => void;
    UpdateApptOps: (inputOps: UpdateApptOpsInp[]) => void;
    previousProperty: () => void;
    clearDecodeData: () => void;
    getDeptSpecs: () => void;
    clearAddVinError: () => void;
    getExCust: (selectedNum: string) => void;
    setAddVehicle: (inp: boolean) => void;
    decodeData: DecodeRec;
    webOpBuilt: boolean;
    waitOnRecall: boolean;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
}

const AddVehicleFooter = (props: IProps) => {
    const { vin, vinValid, makeCode, make, year, model, trim, estMileage } = props.newVehicleInfo!;
    const {selectedVehicle} = props;
    const currentDisplayProperty = props.currentProperty;
    const newVINentryFlag = props.vehicleSpecs!.newVINentryFlag;
    const mainViewPanes = props.mainPaneViewOrder!.map((view): IView => ({ ...view }));
    const currentViewPanes = mainViewPanes[mainViewPanes.length - 1];
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;

    const [gettingData, setGettingData] = useState(false);

    useEffect (() => {
        if (currentViewPanes?.viewId === 'AddVehicle') {
            props.clearMenusState();
        }
    }, [currentViewPanes?.viewId, props.clearMenusState]);

    const isSDAnalyticsEnabled =
        props.vehicleSpecs?.toyotaTrackingFlag === 'Y' ||
        props.vehicleSpecs?.lexusTrackingFlag === 'Y' ||
        props.vehicleSpecs?.mbzTrackingFlag === 'Y' ||
        props.vehicleSpecs?.mazdaCATrackingFlag === 'Y' ||
        props.vehicleSpecs?.vwTrackingFlag === 'Y';

    let showBackButton: boolean = false;
    let showCancelButton: boolean = false;

    switch (newVINentryFlag) {
        case VINEntryFlag.Show:
            showBackButton = currentDisplayProperty !== 'make' || vinValid;
            showCancelButton = !make && !selectedVehicle?.make;
            break;
        case VINEntryFlag.Require:
            showBackButton = vinValid;
            showCancelButton = !vinValid;
            break;
        // default: Act as if the flag was set to Hide.
        case VINEntryFlag.Hide:
        default:
            showBackButton = currentDisplayProperty !== 'make' || vinValid;
            showCancelButton = !make && !selectedVehicle?.make;
    }

    // Disable proceed if we have no mileage or if the vin entered is invalid
    const disableProceed = ((!!!estMileage || (!vinValid && !!vin)) &&
    ((!!!selectedVehicle?.mileage && selectedVehicle?.mileage === 0) ||
    (selectedVehicle?.mileage === props.vehicles[props.vehSelectedVehicle]?.prevMileage &&
        props.vehicles[props.vehSelectedVehicle]?.prevMileage === 0))) ||
        (selectedVehicle?.mileage < props.vehicles[props.vehSelectedVehicle]?.prevMileage && selectedVehicle?.mileage > 0) ||
        (selectedVehicle?.mileage < props.TMNAMileage && selectedVehicle?.mileage > 0);

    const cancelHandler = useCallback(() => {
        props.popFromMainPane!(currentViewPanes?.viewId);
    }, [props.popFromMainPane, currentViewPanes]);

    const backButtonHandler = useCallback((e: any) => {
        props.clearMenusState();
        props.clearAddVinError();
        if (vinValid) {
            props.clearNewVehicle();
            props.clearDecodeData();
            const clearApptVeh = {
                vin: '',
                makeCode: '',
                make: '',
                model: '',
                vehYr: '',
                vehColor: '',
                series: '',
                mileage: 0,
                imagePath: '',
            };
            props.updateAppVehicle(clearApptVeh);
        } else {
            props.previousProperty();
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Back,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'back',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.vehSelectedVehicle);
    }, [
        props?.clearNewVehicle,
        vinValid,
        props.previousProperty,
        props.clearDecodeData,
        props.clearMenusState,
        props.updateAppVehicle,
        props.clearAddVinError,
        props.vehSelectedVehicle,
        props.vehicles,
    ]);

    const cancelBtnHandler = useCallback((e: any) => {
        const vehicleInfo = { ...props.newVehicleInfo! };
        vehicleInfo.vin = '';
        vehicleInfo.vinValid = false;

        cancelHandler();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Cancel,
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'restart',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.vehSelectedVehicle);
    }, [props.newVehicleInfo, cancelHandler, props.vehicles, props.vehSelectedVehicle]);

    const addVehicleHandler = (e?: any) => {
        let mileageStripped = estMileage.replace(/,/g, '');
        // if adding a new vehicle, this takes the entered mileage, if any
        if (mileageStripped === '' && props.newVehicleInfo) {
            mileageStripped = selectedVehicle.mileage.toString();
        }
        if (mileageStripped === strings.Unknown) {
            mileageStripped = '';
        }
        const prevPane = mainViewPanes[mainViewPanes.length - 2]?.viewId;
        if (props.newVehicleInfo?.make) {
            if (isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceVehicleIndicated', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.vehicleSpecs, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                        props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceVehicleMake: props.newVehicleInfo.make,
                    scheduleServiceVehicleMileage: Number(mileageStripped) === -1 ? '' : Number(mileageStripped),
                    scheduleServiceVehicleModel: props.newVehicleInfo.model,
                    scheduleServiceVehicleVin: props.newVehicleInfo.vin,
                    scheduleServiceVehicleYear: props.newVehicleInfo.year,
                });
            }
        } else {
            if (isSDAnalyticsEnabled && prevPane !== 'VehLayout') {
                sendSdEvent('scheduleServiceVehicleIndicated', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.vehicleSpecs, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                        props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceVehicleMake: selectedVehicle.make,
                    scheduleServiceVehicleMileage: selectedVehicle.mileage === -1 ? '' : selectedVehicle.mileage,
                    scheduleServiceVehicleModel: selectedVehicle.model,
                    scheduleServiceVehicleVin: selectedVehicle.vin,
                    scheduleServiceVehicleYear: selectedVehicle.vehYr,
                });
            }
        }

        if (!selectedVehicle?.make || props.newVehicleInfo?.make) {
            // If the VIN wasn't added to the vehicle because of
            // the VINOWNED error. Add it to the appointment using the
            // VINOWNED error text. It will be removed from the appointment on the
            // summary screen if necessary.
            let updateVin = vin;
            if (!updateVin && props.decodeData.decodeErr) {
                updateVin = props.decodeData.decodeErr.split(':')[1];
            }
            const apptNewVehicle: UpdateApptVehInp = {
                vin: updateVin,
                makeCode: makeCode,
                make: make,
                model: model,
                vehYr: year,
                vehColor: '',
                mileage: Number(mileageStripped),
                imagePath: '',
                series: trim,
            };
            props.updateAppVehicle(apptNewVehicle);
            // Should add the new Vehicle to the Vehicle state and then select that Vehicle
            const newStateVehicle: VehicleRec = {
                id: '',
                vin: vin,
                makeCode: makeCode,
                make: make,
                model: model,
                year: year,
                color: '',
                prevMileage: 0,
                prevRoDate: '',
                imagePath: '',
                style: trim,
                nextAppt: '',
                nameId: props.nameId,
                lastAdv: '',
                lastAdvName: '',
                lastAdvImage: '',
                lastTrans: '',
                lastTransLit: '',
            };
            props.addVehicle(newStateVehicle);
            props.clearAdditionalServices();
            props.UpdateApptOps([]);
            setGettingData(true);
        } else if (props.additionalServices.checkedRecalls.length > 0 || props.additionalServices.checkedRecoms.length > 0 ||
            props.additionalServices.checkedWeOwes.length > 0) {
            props.getAdditionalServices(props.specProfile, props.store, props.branch);

            if (selectedVehicle?.make) {
                props.getApptOps(props.specProfile, props.store, props.branch);
            }

            props.getMenus('', '');
            setGettingData(true);
        } else if (!props.additionalServices.allServices.recom[0]) {
            if (props.additionalServices.checkedRecalls.length === 0 && props.additionalServices.checkedRecoms.length === 0 &&
                props.additionalServices.checkedWeOwes.length === 0) {
                props.clearAdditionalServices();
            }
            props.UpdateApptOps([]);
            props.getAdditionalServices(props.specProfile, props.store, props.branch);
            if (selectedVehicle?.make) {
                props.getApptOps(props.specProfile, props.store, props.branch);
            }
            props.getMenus('', '');

            setGettingData(true);
        } else {
            props.clearAdditionalServices();
            props.UpdateApptOps([]);
            props.getAdditionalServices(props.specProfile, props.store, props.branch);
            if (selectedVehicle?.make) {
                props.getApptOps(props.specProfile, props.store, props.branch);
            }
            props.getMenus('', '');
            setGettingData(true);
        }

        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.vehSelectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        if (props.vehSelectedVehicle === 'AddVehicle') {
            const newStateVehicle: VehicleRec = {
                id: '',
                vin: vin,
                makeCode: makeCode,
                make: make,
                model: model,
                year: year,
                color: '',
                prevMileage: 0,
                prevRoDate: '',
                imagePath: '',
                style: trim,
                nextAppt: '',
                nameId: props.nameId,
                lastAdv: '',
                lastAdvName: '',
                lastAdvImage: '',
                lastTrans: '',
                lastTransLit: '',
            };
            const ascRetailProcessInp: ascRetailProcessInput = {
                pageType: 'service',
                flowStage: '3b',
                flowOutcome: 'added',
                flowName: 'appointment',
                elementTitle: 'Add Vehicle',
            };
            ascRetailProcess(ascRetailProcessInp, [newStateVehicle]);
        } else {
            const ascRetailProcessInp: ascRetailProcessInput = {
                pageType: 'service',
                flowStage: '3a',
                flowOutcome: 'proceed',
                flowName: 'appointment',
                elementTitle: 'Select Vehicle',
            };
            ascRetailProcess(ascRetailProcessInp, props.vehicles, props.vehSelectedVehicle);
        }
    };

    useEffect(() => {
        if (props.vehicleAddTriggered) {
            props.setAddVehicle(false);
            addVehicleHandler();
        }
    // eslint-disable-next-line @reynolds/exhaustive-deps
    }, [props.vehicleAddTriggered, props.setAddVehicle]);

    useEffect(() => {
        if (props.addVinError === '0000' && gettingData) {
            props.getAdditionalServices(props.specProfile, props.store, props.branch);
            if (make){
                props.getApptOps(props.specProfile, props.store, props.branch);
            }
            props.getMenus('', '');
        }
    }, [
        props.addVinError,
        props.getMenus,
        make,
        props.branch,
        props.store,
        props.specProfile,
        props.getApptOps,
        props.getAdditionalServices,
        estMileage,
        gettingData,
    ]);

    useEffect(() => {
        // Once we get back from loading allServices and menus, we can proceed.
        if (props.additionalServices.allServices.built && gettingData && props.menusReturnCode !== '' && props.webOpBuilt) {
            setGettingData(false);

            const recomTimes = ({
                'S': props.vehicleSpecs?.svcSpecs?.recommTimes,
                'B': props.vehicleSpecs?.bdySpecs?.recommTimes,
                'Q': props.vehicleSpecs?.ossSpecs?.recommTimes,
            });

            if (props.skipServices) {
                props.getDeptSpecs();

                if (recomTimes[props.apptDept] === 'Y') {
                    props.pushToMainPane({
                        viewId: ComponentMapIds.QuickTime,
                        component: ComponentMapIds.QuickTime,
                    });
                } else {
                    props.pushToMainPane({
                        viewId: ComponentMapIds.CalendarSelect,
                        component: ComponentMapIds.CalendarSelect,
                    });
                }
            } else if (props.vehicleSpecs?.skipServicesFlag && props.additionalServices.checkedRecoms.length === 0 &&
                props.additionalServices.checkedRecalls.length === 0 && props.additionalServices.checkedWeOwes.length === 0) {
                props.pushToMainPane({
                    viewId: ComponentMapIds.AddSkipServices,
                    component: ComponentMapIds.AddSkipServices,
                });
            } else {
                let nextViewId = ComponentMapIds.AdditionalServices;
                let nextComponent = ComponentMapIds.AdditionalServices;
                const maintMake = selectedVehicle ? selectedVehicle.make.toLowerCase().split(' ')[0]:'';
                const maintContractView = (props.countryCode === 'US' &&
                    (((props.lexusMCLaunched || props.lexusTDMCLaunched) && maintMake==='lexus') ||
                    ((props.toyotaMCLaunched || props.toyotaTDMCLaunched) && maintMake==='toyota')));
                if (!props.apptVin || (!props.showWeOwes && !props.showPrvRecom && (!props.showOEMRecall ||
                    !props.inquiryInvoked) && !maintContractView)) {
                    nextViewId = ComponentMapIds.ServiceTab;
                    nextComponent = ComponentMapIds.ServiceTab;
                }
                if (!props.additionalServices.allServices.weOwes[0] && !props.additionalServices.allServices.recom[0] &&
                    !props.additionalServices.allServices.inquiryInvoked && !maintContractView) {
                    nextViewId = ComponentMapIds.ServiceTab;
                    nextComponent = ComponentMapIds.ServiceTab;
                }

                props.pushToMainPane({
                    viewId: nextViewId,
                    component: nextComponent,
                });
            }
            if (props.vehSelectedVehicle !== 'AddVehicle' && !props.login && props.multipleCustomers) {
                props.getExCust(props.vehSelectedVehicle);
            }
        }
    }, [props.additionalServices, props.vehicleSpecs, props.pushToMainPane, props.apptVin, props.showWeOwes,
        props.showPrvRecom, props.showOEMRecall, props.inquiryInvoked, gettingData, props.menusReturnCode,
        props.apptDept, props.getDeptSpecs, props.skipServices, selectedVehicle, props.lexusMCLaunched,
        props.toyotaMCLaunched, props.webOpBuilt, props.countryCode, props.lexusTDMCLaunched, props.toyotaTDMCLaunched,
        props.multipleCustomers, props.getExCust, props.vehSelectedVehicle, props.login]);

    return (
        <div className={css.footer}>
            {(gettingData && !props.waitOnRecall) ?
                <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> :
                null
            }
            <hr className={css.footerHr}></hr>
            <div className={css.btnContainer}>
                <JSXIf Condition={showBackButton}>
                    <div className={css.backBtnDiv} >
                        <SVPButton
                            id='backBtn'
                            message={strings.Back}
                            handler={backButtonHandler}
                            tooltip={strings.BackTitle}
                            variant='secondaryBtnAlt'
                            backArrow={props.theme !== 'lexusMonogram'}
                            theme={props.theme}
                            noArrow={props.theme === 'porsche' ? true : false}
                        />
                    </div>
                </JSXIf>
                <JSXIf Condition={showCancelButton}>
                    <div className={css.cancelBtnDiv}>
                        <SVPButton
                            id='cancelBtn'
                            message={strings.Cancel}
                            handler={cancelBtnHandler}
                            variant='secondaryBtnAlt'
                            noArrow
                            theme={props.theme}
                        />
                    </div>
                </JSXIf>
                <div className={css.proceed}>
                    <SVPButton
                        id='proceedBtn'
                        message={strings.Proceed}
                        handler={addVehicleHandler}
                        isDisabled={disableProceed}
                        tooltip={strings.ProceedTip}
                        theme={props.theme}
                        noArrow={noArrow}
                    />
                </div>
            </div>
        </div >
    );
};

export default AddVehicleFooter;